import { useMutation, useReactiveVar } from "@apollo/client";
import { cloneDeep } from "lodash";
import mixpanel from "mixpanel-browser";

import {
  IPropertyIdVariable,
  IUnitDetailsData,
} from "../../interfaces/interfaces";
import BaseRegistrationModal from "../modals/BaseRegistrationModal";
import { ReactComponent as HomePasscode } from "../../img/HomePasscode.svg";
import {
  GET_HOME_FOR_REGION,
  UPDATE_HOME_PASSCODE,
} from "../../api/gqlQueries";
import { userIdVar } from "../apollo/LocalState";
import { organizationId } from "../../utils/mockConsts";

const HomePasscodeForm: React.FC<{
  homePasscodeValue: string;
  setHomePasscodeValue: React.Dispatch<React.SetStateAction<string>>;
  existingHomePasscode: string;
  closeModal: () => void;
  propertyId: string;
}> = ({
  homePasscodeValue,
  setHomePasscodeValue,
  existingHomePasscode,
  closeModal,
  propertyId,
}) => {
  const userId = useReactiveVar(userIdVar);
  const [updateHomePasscode] = useMutation(UPDATE_HOME_PASSCODE);
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    updateHomePasscode({
      update: (cache, { data }) => {
        if (!data) {
          // No need to update cache if no data returned.
          return;
        }
        const cachedData = cache.readQuery<
          IUnitDetailsData,
          IPropertyIdVariable
        >({
          query: GET_HOME_FOR_REGION,
          variables: { propertyId },
        });
        if (!cachedData) {
          return;
        }
        const updatedData = cloneDeep(cachedData);
        updatedData.home.homePasscode = data.updateHomePasscode.homePasscode;
        cache.writeQuery({
          data: updatedData,
          query: GET_HOME_FOR_REGION,
          variables: { propertyId },
        });
        closeModal();
      },
      variables: { homePasscode: homePasscodeValue, propertyId },
    });
    mixpanel.track("Home Passcode Changed", {
      organization_id: organizationId,
      property_id: propertyId,
      user_id: userId,
    });
    setHomePasscodeValue("");
  };
  return (
    <form onSubmit={handleSubmit}>
      <div className="modal-create-body-inner">
        <div className="modal-input-container">
          <label
            htmlFor="existing-home-passcode"
            data-testid="existing-home-passcode"
          >
            Existing Home Passcode
          </label>
          <input
            className="modal-input-existing"
            name="existing-home-passcode"
            value={existingHomePasscode}
            readOnly
            disabled
          />
        </div>
        <div className="modal-input-container">
          <label htmlFor="new-home-passcode" data-testid="new-home-passcode">
            New Home Passcode
          </label>
          <input
            className="modal-input"
            name="new-home-passcode"
            maxLength={4}
            value={homePasscodeValue}
            onChange={(e) => {
              // Only allow numeric characters
              const numericValue = e.target.value.replace(/[^0-9]/g, "");

              // Limit to 4 characters
              const limitedValue = numericValue.slice(0, 4);

              setHomePasscodeValue(limitedValue);
            }}
          />
        </div>
      </div>
      <div className="margin-bottom-small" />
      <div className="modal-input-actions-delete">
        <button
          type="button"
          className="tertiary-btn-small tertiary-btn-small-alert"
          onClick={closeModal}
        >
          Cancel
        </button>
        <button
          disabled={
            homePasscodeValue.length !== 4 ||
            homePasscodeValue === existingHomePasscode
          }
          type="submit"
          className="tertiary-btn-small tertiary-btn-small-main-alert tertiary-btn-small-main"
        >
          Change Home Passcode
        </button>
      </div>
    </form>
  );
};

interface HomePasscodeModalProps {
  propertyId: string;
  homePasscodeValue: string;
  setHomePasscodeValue: React.Dispatch<React.SetStateAction<string>>;
  existingHomePasscode: string;
  closeModal: () => void;
}

const HomePasscodeModal: React.FC<HomePasscodeModalProps> = ({
  propertyId,
  closeModal,
  homePasscodeValue,
  setHomePasscodeValue,
  existingHomePasscode,
}) => {
  return (
    <BaseRegistrationModal
      closeModalFn={closeModal}
      headerText="Home Passcode"
      Image={<HomePasscode className="organization-create-img" />}
    >
      <div className="tenants-show-description-container">
        All Homes have a default 4-digit home passcode. Home Members may
        customize their home passcode once they join the Home.
      </div>
      <div className="margin-top-small" />
      <HomePasscodeForm
        closeModal={closeModal}
        homePasscodeValue={homePasscodeValue}
        setHomePasscodeValue={setHomePasscodeValue}
        existingHomePasscode={existingHomePasscode}
        propertyId={propertyId}
      />
    </BaseRegistrationModal>
  );
};

export default HomePasscodeModal;
